import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Card, Col, Row } from 'reactstrap';
import { Button, TextAreaInput, TextField, addToast } from '@octano/global-ui';
import { useForm } from 'react-hook-form';

import GoBackToListButton from '../../../../../components/buttons/GoBackToListButton';
import DisplayError from '../../../../../components/info/DisplayError';
import Loading from '../../../../../components/info/Loading';
import { TranslationsKeys } from '../../../../../locales/translations';
import {
  getPreviewDocument,
  tokenSignPreviewDocument,
} from '../../../../../api/requests/tuitionProcess';
import { RequestedDocument } from '../../../../../types/signDocumentationTypes';
import {
  getHomologationRequestDetail,
  getRequestDocuments,
  downloadActaPdf,
  downloadCertificadoActaPdf,
} from '../../../Request/api';
import {
  HomologationRequestDetail,
  HomologationSelectedCourse,
} from '../../../Request/types';
import HomologationCoursesTable from './components/HomologationTable';
import HomologationModal from '../../../Request/views/Detail/components/HomologationModal';
import DocumentationSection from './components/DocumentationSection';
import AuthorizationModal from './components/AuthorizationModal';
import { useValidations } from '../../../../../hooks/useValidations';

interface AuthFormData {
  observation: string;
}

const MAX_OBSERVATION_LENGTH = 200;

export default function HomologationAuthorize() {
  const { t } = useTranslation(
    TranslationsKeys.HOMOLOGATION_AUTHORIZATION_DETAIL,
  );
  const { requestId } = useParams<{ requestId: string }>();
  const history = useHistory();
  const { msgValidations } = useValidations();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState<HomologationRequestDetail | null>(null);
  const [requestedDocuments, setRequestedDocuments] = useState<
    RequestedDocument[]
  >([]);

  // Modal states
  const [
    modalCourse,
    setModalCourse,
  ] = useState<HomologationSelectedCourse | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [downloadingActa, setDownloadingActa] = useState(false);
  const [downloadingCertificado, setDownloadingCertificado] = useState(false);

  const { control, getValues } = useForm<AuthFormData>({
    defaultValues: { observation: '' },
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(false);
    const result = await getHomologationRequestDetail(Number(requestId));
    if (result.error) {
      setError(true);
      setLoading(false);
      return;
    }

    setData(result.data ?? null);

    // Fetch generic documents from documentation step endpoint
    if (result.data?.postulationDetailId) {
      const docsResult = await getRequestDocuments(
        result.data.postulationDetailId,
        'general-docs',
      );
      if (!docsResult.error && docsResult.data?.requestedDocuments) {
        setRequestedDocuments(docsResult.data.requestedDocuments);
      }
    }

    setLoading(false);
  }, [requestId]);

  const refreshData = useCallback(async () => {
    const result = await getHomologationRequestDetail(Number(requestId));
    if (!result.error) {
      setData(result.data ?? null);
    }
  }, [requestId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleHomologate = useCallback((course: HomologationSelectedCourse) => {
    setModalCourse(course);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalCourse(null);
  }, []);

  const handleDownloadDocument = useCallback(
    async (reqDoc: RequestedDocument) => {
      if (!data?.postulationDetailId) return;
      const { data: token, error: tokenError } = await tokenSignPreviewDocument(
        data.postulationDetailId,
        reqDoc.document.code,
      );
      if (token && !tokenError) {
        const url = getPreviewDocument(data.postulationDetailId, token);
        window.open(url);
      } else {
        addToast({
          icon: 'error',
          color: 'danger',
          text: 'Error al descargar el documento.',
        });
      }
    },
    [data],
  );

  const handleDownloadActa = useCallback(async () => {
    if (!data) return;
    setDownloadingActa(true);
    const result = await downloadActaPdf(data.responseId);
    setDownloadingActa(false);
    if (result.error || !result.data) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: 'Error al descargar el acta de homologación.',
      });
      return;
    }
    const url = window.URL.createObjectURL(result.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `acta-homologacion-${data.responseId}.pdf`;
    link.click();
    window.URL.revokeObjectURL(url);
  }, [data]);

  const handleDownloadCertificado = useCallback(async () => {
    if (!data) return;
    setDownloadingCertificado(true);
    const result = await downloadCertificadoActaPdf(data.responseId);
    setDownloadingCertificado(false);
    if (result.error || !result.data) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: 'Error al descargar el certificado.',
      });
      return;
    }
    const url = window.URL.createObjectURL(result.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `certificado-acta-homologacion-${data.responseId}.pdf`;
    link.click();
    window.URL.revokeObjectURL(url);
  }, [data]);

  const handleAuthorizeClick = useCallback(() => {
    setShowAuthModal(true);
  }, []);

  const handleAuthSuccess = useCallback(() => {
    history.push('/authorization-requests');
  }, [history]);

  if (loading) {
    return (
      <div className="mx-3">
        <Loading insideCard />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-3">
        <DisplayError
          insideCard
          title={t('error.title')}
          textBody={t('error.body')}
          retryAction={fetchData}
        />
      </div>
    );
  }

  const isReadOnly = data.resolution !== 'pending';

  return (
    <div className="mx-3">
      <Card className="p-4">
        {/* Header with go back + student info */}
        <Row className="pb-3 pb-lg-4 align-items-center">
          <Col xs={12} lg={6} className="pb-3">
            <GoBackToListButton path="/authorization-requests" />
          </Col>
          <Col xs={12} lg={4}>
            <Row>
              <Col xs={12} lg={6} className="pr-lg-0 mb-n2">
                <TextField
                  placeholder={t('studentInfo.postulant')}
                  border={false}
                  style={{ borderRadius: '0px' }}
                >
                  {data.studentName}
                </TextField>
              </Col>
              <Col xs={12} lg={6} className="pl-lg-0 mb-n2 pr-lg-0">
                <TextField
                  placeholder={t('studentInfo.status')}
                  border={false}
                  style={{ borderRadius: '0px' }}
                >
                  {t(`statusLabels.${data.status}` as any) ?? data.status}
                </TextField>
              </Col>
            </Row>
          </Col>
          <Col xs={12} lg={2} className="mb-n2">
            <TextField placeholder={t('studentInfo.admissionType')}>
              {data.admissionTypeName ?? '-'}
            </TextField>
          </Col>
        </Row>

        {/* Documentation section */}
        <DocumentationSection
          requestedDocuments={requestedDocuments}
          postulationDetailId={data.postulationDetailId}
          onDownload={handleDownloadDocument}
          onDownloadAll={async () => {
            const uploaded = requestedDocuments.filter((d) => d.uploaded);
            await Promise.all(uploaded.map((d) => handleDownloadDocument(d)));
          }}
        />

        {/* Courses validation table */}
        <HomologationCoursesTable
          courses={data.selectedCourses}
          requestId={data.responseId}
          onHomologate={handleHomologate}
          onFileChange={refreshData}
          readOnly={isReadOnly}
        />

        {/* Observation textarea */}
        {!isReadOnly && (
          <div className="mt-4">
            <TextAreaInput
              name="observation"
              label={t('observation')}
              control={control}
              placeholder={t('observationPlaceholder')}
              maxLength={MAX_OBSERVATION_LENGTH}
              rules={{
                required: msgValidations.required,
                maxLength: msgValidations.invalidMaxLength(
                  MAX_OBSERVATION_LENGTH,
                ),
              }}
            />
          </div>
        )}

        {/* Download buttons */}
        <div
          className="d-flex justify-content-end mt-4"
          style={{ gap: '1rem' }}
        >
          <div style={{ width: 280 }}>
            <Button
              text={t('downloadActa')}
              icon="download"
              outlined
              fullwidth
              onClick={handleDownloadActa}
              loading={downloadingActa}
            />
          </div>
          <div style={{ width: 320 }}>
            <Button
              text={t('downloadCertificado')}
              icon="download"
              outlined
              fullwidth
              onClick={handleDownloadCertificado}
              loading={downloadingCertificado}
            />
          </div>
        </div>

        {/* Footer buttons */}
        {!isReadOnly && (
          <Row className="pt-4 pb-2 justify-content-center">
            <Col
              xs={12}
              sm={{
                size: 4,
                offset: 4,
              }}
              className="mb-2"
            >
              <Button
                text={t('cancel')}
                outlined
                fullwidth
                onClick={() => history.push('/authorization-requests')}
              />
            </Col>
            <Col xs={12} sm={4} className="mb-2">
              <Button
                text={t('authorize')}
                fullwidth
                onClick={handleAuthorizeClick}
              />
            </Col>
          </Row>
        )}
      </Card>

      {/* Homologation per-course Modal (reused from Detail) */}
      {!isReadOnly && (
        <HomologationModal
          isOpen={!!modalCourse}
          onClose={handleModalClose}
          course={modalCourse}
          requestId={data.responseId}
          onSuccess={refreshData}
        />
      )}

      {/* Authorization Modal */}
      {!isReadOnly && (
        <AuthorizationModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          requestId={data.responseId}
          observation={getValues('observation')}
          onSuccess={handleAuthSuccess}
        />
      )}
    </div>
  );
}
